import { BackendService, CacheEntry } from "../api/v1/api_pb";
import React, { useEffect } from "react";
import {
  generatePath,
  Link as RouterLink,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import prettyBytes from "pretty-bytes";
import FontAwesomeIcon from "../icons/FontAwesomeIcon";
import { faApple, faLinux } from "@fortawesome/free-brands-svg-icons";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  gridRowSelectionIdsSelector,
  GridToolbarQuickFilter,
  GridValidRowModel,
  Toolbar,
  ToolbarButton,
  useGridApiRef,
} from "@mui/x-data-grid";
import { Delete } from "@mui/icons-material";
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from "@connectrpc/connect-query";
import {
  deleteCacheEntries,
  listCacheEntries,
} from "../api/v1/api-BackendService_connectquery";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { isEqual, joinURL, parseFilename } from "ufo";
import { Code } from "@connectrpc/connect";
import { timestampDate } from "@bufbuild/protobuf/wkt";

export default function RunnersDashboard() {
  const params = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();

  const { data, isFetching, error } = useQuery(
    listCacheEntries,
    {
      organization: params.organization,
      prefix: params["*"],
    },
    {
      // There's no easy/reliable way to check for staleness when using
      // ListObjectsV2, so let's just disable the automatic refetch
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (error != null) {
      if (error.code === Code.Unauthenticated) {
        window.location.replace("/login/github");

        return;
      }

      enqueueSnackbar(error.rawMessage, {
        variant: "error",
      });
    }
  }, [enqueueSnackbar, error]);

  const { mutate } = useMutation(deleteCacheEntries, {
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: BackendService.method.listCacheEntries,
          cardinality: "finite",
        }),
      });
    },
    onError: async (error) => {
      enqueueSnackbar(error.rawMessage, {
        variant: "error",
      });
    },
  });

  let basePath = generatePath("/dashboard/:organization/cache", {
    organization: params.organization!,
  });
  let { pathname: currentPath } = useLocation();

  const rows =
    data?.entries.map((row: CacheEntry) => {
      return {
        id: row.key,
        name: parseFilename(row.key),
        isDirectory: row.isDirectory,
        sizeBytes: row.sizeBytes,
        lastModified: row.lastModified
          ? timestampDate(row.lastModified)
          : undefined,
      };
    }) ?? [];

  const columns: GridColDef[] = [
    {
      field: "name",
      type: "string",
      headerName: "Name",
      flex: 8,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.isDirectory) {
          return (
            <Link
              component={RouterLink}
              to={joinURL(currentPath, params.row.name)}
            >
              {isEqual(basePath, currentPath)
                ? renderSpecialDirectory(params.row.name)
                : params.formattedValue}
            </Link>
          );
        }

        return params.formattedValue;
      },
    },
    {
      field: "lastModified",
      type: "dateTime",
      headerName: "Last modified",
      flex: 2,
      valueFormatter: (lastModified?: Date) => {
        if (!lastModified) {
          return "—";
        }

        return new Intl.DateTimeFormat(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(lastModified);
      },
    },
    {
      field: "sizeBytes",
      type: "number",
      headerName: "Size",
      flex: 1,
      valueFormatter: (size: number, params: any) => {
        if (params.isDirectory) {
          return "—";
        }

        return prettyBytes(size);
      },
    },
  ];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState<GridValidRowModel[]>(
    [],
  );

  const handleDialogClosed = (confirmed: boolean) => {
    setDialogOpen(false);

    if (!confirmed) {
      return;
    }

    mutate({
      organization: params.organization!,
      keys: selectedRows.map((item) => item.id),
    });
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Dialog
        open={dialogOpen}
        onClose={() => handleDialogClosed(false)}
        maxWidth="xs"
      >
        <DialogTitle>
          Delete {selectedRows.length} cache{" "}
          {selectedRows.length === 1 ? "entry" : "entries"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action is irreversible and will permanently remove the selected
            cache {selectedRows.length === 1 ? "entry" : "entries"}. Proceed
            with deletion?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClosed(false)}>Cancel</Button>
          <Button onClick={() => handleDialogClosed(true)} color={"error"}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <DataGrid
        // Expose API reference for retrieving selected rows
        apiRef={apiRef}
        // Display loading bar when data is being fetched
        loading={isFetching}
        // Provide data to render
        rows={rows}
        columns={columns}
        // Provide a couple of sane page size options
        pageSizeOptions={[25, 50]}
        // Enable selection, but make directories unselectable
        checkboxSelection
        isRowSelectable={(params) => !params.row.isDirectory}
        // Subscribe to selection changes and remember them
        onRowSelectionModelChange={() => {
          setSelectedRows(
            Array.from(gridRowSelectionIdsSelector(apiRef).values()),
          );
        }}
        // Toolbar
        showToolbar
        slots={{
          toolbar: CacheToolbar,
        }}
        slotProps={{
          toolbar: {
            breadcrumbs: craftBreadcrumbs(basePath, params),
            onDelete: () => setDialogOpen(true),
            disabled: selectedRows.length === 0,
          } as any,
        }}
      />
    </Container>
  );
}

function CacheToolbar({ breadcrumbs, onDelete, disabled }: any) {
  return (
    <Toolbar>
      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}
      >
        <Breadcrumbs separator="›" sx={{ p: 1 }}>
          {breadcrumbs.map((breadcrumb: any) => (
            <div>{breadcrumb}</div>
          ))}
        </Breadcrumbs>
        <Box sx={{ display: "flex" }}>
          <ToolbarButton onClick={onDelete} disabled={disabled}>
            <Delete />
          </ToolbarButton>
          <GridToolbarQuickFilter />
        </Box>
      </Box>
    </Toolbar>
  );
}

function craftBreadcrumbs(basePath: string, params: any) {
  let breadcrumbs = [
    <Link component={RouterLink} to={basePath}>
      Cache entries
    </Link>,
  ];

  const segments = params["*"]?.split("/").filter(Boolean) ?? [];

  segments.forEach((item: string, index: number) => {
    const relativePath = segments.slice(0, index + 1).join("/");

    breadcrumbs.push(
      <Link component={RouterLink} to={joinURL(basePath, relativePath)}>
        {index === 0 ? renderSpecialDirectory(item) : item}
      </Link>,
    );
  });

  return breadcrumbs;
}

function renderSpecialDirectory(name: string) {
  switch (name) {
    case "macos":
      return (
        <span>
          <FontAwesomeIcon
            icon={faApple}
            style={{ verticalAlign: "-0.25em" }}
          />
          macOS and{" "}
          <FontAwesomeIcon
            icon={faLinux}
            style={{ verticalAlign: "-0.25em" }}
          />
          Linux (<InlineCode>arm64</InlineCode>) runners cache
        </span>
      );
    case "linux":
      return (
        <span>
          <FontAwesomeIcon
            icon={faLinux}
            style={{ verticalAlign: "-0.25em" }}
          />
          Linux (<InlineCode>amd64</InlineCode>) runners cache
        </span>
      );
    default:
      return <span>{name}</span>;
  }
}

const InlineCode = ({ children }: any) => {
  return (
    <Typography component={"code"} fontFamily={"monospace"} fontSize={14}>
      {children}
    </Typography>
  );
};
